import { useState, useEffect } from "react";
import axios from "axios";
import { Scissors, MapPin, Clock, ChevronRight, ChevronLeft, Check, Calendar, User, CreditCard, ShieldCheck, Lock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { format, addDays } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Steps:
// 1=location, 2=service, 3=provider, 4=time, 5=info,
// 6=card (only for new/card-less clients), 7=confirmed
export default function ClientBookingPage() {
  const [step, setStep] = useState(1);
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedDate, setSelectedDate] = useState(format(addDays(new Date(), 1), "yyyy-MM-dd"));
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [clientInfo, setClientInfo] = useState({ name: "", email: "", phone: "", notes: "" });
  const [clientStatus, setClientStatus] = useState(null); // {exists, has_card, client_id}
  const [cardForm, setCardForm] = useState({ card_number: "", exp_month: "", exp_year: "", cardholder_name: "", cvc: "" });
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/locations`).then(res => setLocations(res.data || []));
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      axios.get(`${API}/public/services?location_id=${selectedLocation.location_id}`).then(res => setServices(res.data || []));
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (selectedLocation && selectedService) {
      axios.get(`${API}/public/providers?location_id=${selectedLocation.location_id}&service_id=${selectedService.service_id}`).then(res => setProviders(res.data || []));
    }
  }, [selectedLocation, selectedService]);

  useEffect(() => {
    if (selectedLocation && selectedService && selectedDate) {
      const provParam = selectedProvider ? `&provider_id=${selectedProvider.user_id}` : "";
      axios
        .get(`${API}/public/available-slots?location_id=${selectedLocation.location_id}&service_id=${selectedService.service_id}&date=${selectedDate}${provParam}`)
        .then(res => setSlots(res.data?.slots || []));
    }
  }, [selectedLocation, selectedService, selectedProvider, selectedDate]);

  // After client fills info, check status
  const proceedFromInfo = async () => {
    if (!clientInfo.name || !clientInfo.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setChecking(true);
    try {
      const res = await axios.get(`${API}/public/client-status`, { params: { email: clientInfo.email } });
      setClientStatus(res.data);
      if (res.data.has_card) {
        // Existing client with card → skip card step and book directly
        await finalizeBooking();
      } else {
        // New client OR existing without card → require card
        setStep(6);
      }
    } catch {
      toast.error("We couldn't verify your account. Please try again.");
    }
    setChecking(false);
  };

  const registerCardThenBook = async () => {
    const digits = (cardForm.card_number || "").replace(/\D/g, "");
    if (digits.length < 13) {
      toast.error("Please enter a valid card number.");
      return;
    }
    const m = parseInt(cardForm.exp_month);
    const y = parseInt(cardForm.exp_year);
    if (!m || m < 1 || m > 12) {
      toast.error("Please enter a valid expiration month.");
      return;
    }
    if (!y || y < new Date().getFullYear()) {
      toast.error("Please enter a valid expiration year.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/public/register-card`, {
        client_name: clientInfo.name,
        client_email: clientInfo.email,
        client_phone: clientInfo.phone,
        card_number: digits,
        exp_month: m,
        exp_year: y,
        cardholder_name: cardForm.cardholder_name || clientInfo.name,
      });
      await finalizeBooking();
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't save your card. Please double-check and try again.");
      setLoading(false);
    }
  };

  const finalizeBooking = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/book`, {
        location_id: selectedLocation.location_id,
        service_id: selectedService.service_id,
        provider_id: selectedSlot.provider_id,
        date: selectedDate,
        time: selectedSlot.time,
        client_name: clientInfo.name,
        client_email: clientInfo.email,
        client_phone: clientInfo.phone,
        notes: clientInfo.notes,
      });
      setBooking(res.data);
      setStep(7);
      toast.success("Booking confirmed!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    }
    setLoading(false);
  };

  // Group slots by provider
  const groupedSlots = {};
  slots.forEach(s => {
    if (!groupedSlots[s.provider_name]) groupedSlots[s.provider_name] = [];
    groupedSlots[s.provider_name].push(s);
  });

  const stepCount = 6;
  const displayStep = step === 7 ? 6 : Math.min(step, stepCount);

  return (
    <div className="bg-gradient-to-b from-rose-50/30 via-white to-white" data-testid="client-booking-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        {/* Progress */}
        {step !== 7 && (
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-10 overflow-x-auto" data-testid="booking-progress">
            {Array.from({ length: stepCount }, (_, i) => i + 1).map(s => (
              <div key={s} className={`flex items-center gap-1.5 sm:gap-2 ${s <= displayStep ? "text-slate-900" : "text-slate-300"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors font-['Outfit'] ${
                  s < displayStep
                    ? "bg-slate-900 border-slate-900 text-white"
                    : s === displayStep
                    ? "border-rose-500 text-slate-900 bg-white"
                    : "border-slate-200 text-slate-300 bg-white"
                }`}>
                  {s < displayStep ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : s}
                </div>
                {s < stepCount && <div className={`w-4 sm:w-8 h-0.5 ${s < displayStep ? "bg-slate-900" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Location */}
        {step === 1 && (
          <div>
            <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight mb-2">Choose a location</h2>
            <p className="text-slate-500 font-['Manrope'] mb-8">Pick the salon nearest to you.</p>
            <div className="space-y-3">
              {locations.map(loc => (
                <button key={loc.location_id} data-testid={`book-location-${loc.location_id}`}
                  onClick={() => { setSelectedLocation(loc); setStep(2); }}
                  className="w-full bg-white border border-slate-100 rounded-2xl p-5 text-left hover:border-rose-300 hover:shadow-md transition-all flex items-center justify-between">
                  <div>
                    <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{loc.name}</p>
                    {loc.address && <p className="text-sm text-slate-500 mt-0.5 font-['Manrope']">{loc.address}</p>}
                    {loc.phone && <p className="text-xs text-slate-400 mt-0.5 font-['Manrope']">{loc.phone}</p>}
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Service */}
        {step === 2 && (
          <div>
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4 font-['Manrope']"><ChevronLeft className="w-4 h-4" />Back</button>
            <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight mb-2">Select a service</h2>
            <p className="text-slate-500 font-['Manrope'] mb-8">at {selectedLocation?.name}</p>
            {Object.entries(services.reduce((acc, s) => { (acc[s.category || "Other"] = acc[s.category || "Other"] || []).push(s); return acc; }, {})).map(([cat, svcs]) => (
              <div key={cat} className="mb-7">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope']">{cat}</h3>
                <div className="space-y-2">
                  {svcs.map(svc => (
                    <button key={svc.service_id} data-testid={`book-service-${svc.service_id}`}
                      onClick={() => { setSelectedService(svc); setStep(3); }}
                      className="w-full bg-white border border-slate-100 rounded-2xl p-4 text-left hover:border-rose-300 hover:shadow-md transition-all flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-1 h-10 rounded-full" style={{ backgroundColor: svc.color || "#F43F5E" }} />
                        <div>
                          <p className="text-sm font-semibold text-slate-900 font-['Outfit']">{svc.name}</p>
                          <p className="text-xs text-slate-400 font-['Manrope']">{svc.duration}min</p>
                        </div>
                      </div>
                      <span className="text-lg font-semibold text-slate-900 font-['Outfit']">${svc.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 3: Provider */}
        {step === 3 && (
          <div>
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4 font-['Manrope']"><ChevronLeft className="w-4 h-4" />Back</button>
            <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight mb-2">Choose a provider</h2>
            <p className="text-slate-500 font-['Manrope'] mb-8">{selectedService?.name} at {selectedLocation?.name}</p>
            <button data-testid="book-any-provider" onClick={() => { setSelectedProvider(null); setStep(4); }}
              className="w-full bg-white border border-slate-100 rounded-2xl p-5 text-left hover:border-rose-300 hover:shadow-md transition-all mb-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-rose-50 flex items-center justify-center"><User className="w-5 h-5 text-rose-500" /></div>
                <div><p className="text-sm font-semibold text-slate-900 font-['Outfit']">First Available</p><p className="text-xs text-slate-400 font-['Manrope']">Any qualified provider</p></div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
            {providers.map(p => (
              <button key={p.user_id} data-testid={`book-provider-${p.user_id}`}
                onClick={() => { setSelectedProvider(p); setStep(4); }}
                className="w-full bg-white border border-slate-100 rounded-2xl p-5 text-left hover:border-rose-300 hover:shadow-md transition-all mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold font-['Outfit']" style={{ backgroundColor: p.color || "#B5A493" }}>{p.name?.[0]}</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 font-['Outfit']">{p.name}</p>
                    <p className="text-xs text-slate-400 font-['Manrope']">{p.title}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            ))}
          </div>
        )}

        {/* Step 4: Date & Time */}
        {step === 4 && (
          <div>
            <button onClick={() => setStep(3)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4 font-['Manrope']"><ChevronLeft className="w-4 h-4" />Back</button>
            <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight mb-2">Pick a time</h2>
            <p className="text-slate-500 font-['Manrope'] mb-8">
              {selectedService?.name} ({selectedService?.duration}min) {selectedProvider ? `with ${selectedProvider.name}` : "with any provider"}
            </p>
            <div className="mb-6">
              <label className="text-xs font-semibold text-slate-500 mb-3 block uppercase tracking-widest font-['Manrope']">Date</label>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {Array.from({ length: 14 }, (_, i) => addDays(new Date(), i + 1)).map(d => {
                  const ds = format(d, "yyyy-MM-dd");
                  const isSelected = ds === selectedDate;
                  return (
                    <button key={ds} onClick={() => setSelectedDate(ds)} data-testid={`book-date-${ds}`}
                      className={`flex-shrink-0 w-16 py-3 rounded-2xl text-center transition-all ${
                        isSelected ? "bg-slate-900 text-white" : "bg-white border border-slate-100 hover:border-rose-300"
                      }`}>
                      <p className={`text-[10px] font-bold uppercase font-['Manrope'] ${isSelected ? "text-white/70" : "text-slate-400"}`}>{format(d, "EEE")}</p>
                      <p className={`text-lg font-semibold font-['Outfit'] ${isSelected ? "text-white" : "text-slate-900"}`}>{format(d, "d")}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-3 block uppercase tracking-widest font-['Manrope']">Available Times</label>
              {Object.keys(groupedSlots).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-10 bg-white rounded-2xl border border-slate-100 font-['Manrope']">No available slots for this date. Try another day.</p>
              ) : (
                Object.entries(groupedSlots).map(([provName, provSlots]) => (
                  <div key={provName} className="mb-4">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 font-['Manrope']">{provName}</p>
                    <div className="flex flex-wrap gap-2">
                      {provSlots.map((slot, i) => {
                        const isSelected = selectedSlot?.time === slot.time && selectedSlot?.provider_id === slot.provider_id;
                        return (
                          <button key={i} data-testid={`book-slot-${slot.provider_id}-${slot.time}`}
                            onClick={() => setSelectedSlot(slot)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all font-['Manrope'] ${
                              isSelected ? "bg-slate-900 text-white" : "bg-white border border-slate-100 hover:border-rose-300 text-slate-900"
                            }`}>
                            {slot.time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
            {selectedSlot && (
              <Button data-testid="book-continue-btn" onClick={() => setStep(5)} className="w-full mt-6 bg-slate-900 text-white hover:bg-slate-800 rounded-full h-12 text-base font-semibold">
                Continue
              </Button>
            )}
          </div>
        )}

        {/* Step 5: Client Info */}
        {step === 5 && (
          <div>
            <button onClick={() => setStep(4)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4 font-['Manrope']"><ChevronLeft className="w-4 h-4" />Back</button>
            <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight mb-2">Your information</h2>
            <p className="text-slate-500 font-['Manrope'] mb-6">We'll send your confirmation to this email.</p>

            <BookingSummary
              location={selectedLocation}
              service={selectedService} provider={selectedSlot?.provider_name}
              date={selectedDate} time={selectedSlot?.time}
            />

            <div className="space-y-3">
              <Input data-testid="book-name" value={clientInfo.name} onChange={e => setClientInfo(p => ({ ...p, name: e.target.value }))}
                placeholder="Full name" className="h-12 rounded-xl border-slate-200 font-['Manrope']" />
              <Input data-testid="book-email" type="email" value={clientInfo.email} onChange={e => setClientInfo(p => ({ ...p, email: e.target.value }))}
                placeholder="Email address" className="h-12 rounded-xl border-slate-200 font-['Manrope']" />
              <Input data-testid="book-phone" value={clientInfo.phone} onChange={e => setClientInfo(p => ({ ...p, phone: e.target.value }))}
                placeholder="Phone (optional)" className="h-12 rounded-xl border-slate-200 font-['Manrope']" />
              <Input data-testid="book-notes" value={clientInfo.notes} onChange={e => setClientInfo(p => ({ ...p, notes: e.target.value }))}
                placeholder="Anything your stylist should know? (optional)" className="h-12 rounded-xl border-slate-200 font-['Manrope']" />
              <Button data-testid="book-continue-info" onClick={proceedFromInfo} disabled={checking}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-12 text-base font-semibold mt-2">
                {checking ? "Checking…" : "Continue"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Card on File */}
        {step === 6 && (
          <div data-testid="book-card-step">
            <button onClick={() => setStep(5)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4 font-['Manrope']"><ChevronLeft className="w-4 h-4" />Back</button>
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight">Card on file</h2>
                <p className="text-slate-500 font-['Manrope'] mt-1">
                  {clientStatus?.exists ? "Welcome back! Please add a payment method to complete your booking." : "To protect our stylists' time, we keep a card on file for all new clients. You won't be charged until checkout."}
                </p>
              </div>
            </div>

            <BookingSummary
              location={selectedLocation}
              service={selectedService} provider={selectedSlot?.provider_name}
              date={selectedDate} time={selectedSlot?.time}
            />

            <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-semibold text-emerald-700 font-['Manrope']">Secure payment — encrypted & PCI-compliant</p>
              </div>
              <Input data-testid="book-card-holder" value={cardForm.cardholder_name}
                onChange={e => setCardForm(f => ({ ...f, cardholder_name: e.target.value }))}
                placeholder="Name on card" className="h-12 rounded-xl border-slate-200 font-['Manrope']" />
              <div className="relative">
                <Lock className="w-3.5 h-3.5 text-slate-300 absolute right-4 top-1/2 -translate-y-1/2" />
                <Input data-testid="book-card-number" inputMode="numeric" value={cardForm.card_number}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 16);
                    const masked = v.match(/.{1,4}/g)?.join(" ") || v;
                    setCardForm(f => ({ ...f, card_number: masked }));
                  }}
                  placeholder="Card number" className="h-12 rounded-xl border-slate-200 font-['Manrope'] font-mono tracking-wider pr-10" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input data-testid="book-card-exp-month" inputMode="numeric" value={cardForm.exp_month}
                  onChange={e => setCardForm(f => ({ ...f, exp_month: e.target.value.replace(/\D/g, "").slice(0, 2) }))}
                  placeholder="MM" className="h-12 rounded-xl border-slate-200 font-['Manrope']" />
                <Input data-testid="book-card-exp-year" inputMode="numeric" value={cardForm.exp_year}
                  onChange={e => setCardForm(f => ({ ...f, exp_year: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                  placeholder="YYYY" className="h-12 rounded-xl border-slate-200 font-['Manrope']" />
                <Input data-testid="book-card-cvc" inputMode="numeric" value={cardForm.cvc}
                  onChange={e => setCardForm(f => ({ ...f, cvc: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                  placeholder="CVC" className="h-12 rounded-xl border-slate-200 font-['Manrope']" />
              </div>
              <p className="text-[11px] text-slate-400 font-['Manrope'] leading-relaxed pt-1">
                By continuing, you agree to our cancellation policy. A $25 no-show fee may apply if you cancel within 24 hours.
              </p>
              <Button data-testid="book-save-card-confirm" onClick={registerCardThenBook} disabled={loading}
                className="w-full bg-gradient-to-r from-rose-500 to-violet-500 hover:from-rose-600 hover:to-violet-600 text-white rounded-full h-12 text-base font-semibold mt-2">
                {loading ? "Confirming…" : "Save Card & Confirm Booking"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 7: Confirmed */}
        {step === 7 && booking && (
          <div className="text-center py-10" data-testid="book-confirmed">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight mb-2">You're booked!</h2>
            <p className="text-slate-500 font-['Manrope'] mb-8">We've sent a confirmation to {clientInfo.email}</p>
            <div className="bg-white border border-slate-100 rounded-2xl p-6 max-w-sm mx-auto text-left space-y-2.5 shadow-sm">
              <Row label="Service" value={booking.service_names?.join(", ")} />
              <Row label="Provider" value={booking.provider_name} />
              <Row label="Location" value={selectedLocation?.name} />
              {selectedLocation?.address && <Row label="Address" value={selectedLocation.address} />}
              <Row label="Date" value={booking.start_time ? new Date(booking.start_time).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : ""} />
              <Row label="Time" value={booking.start_time ? new Date(booking.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""} />
              <Row label="Booking ID" value={booking.appointment_id} mono />
            </div>
            <Button data-testid="book-another-btn" onClick={() => {
              setStep(1); setBooking(null); setSelectedSlot(null); setSelectedProvider(null);
              setSelectedService(null); setSelectedLocation(null);
              setClientInfo({ name: "", email: "", phone: "", notes: "" });
              setCardForm({ card_number: "", exp_month: "", exp_year: "", cardholder_name: "", cvc: "" });
              setClientStatus(null);
            }} className="mt-8 bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11 px-6 text-sm font-semibold">
              Book another appointment
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function BookingSummary({ location, service, provider, date, time }) {
  if (!service) return null;
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 mb-5" data-testid="booking-summary">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope']">Your booking</p>
      <div className="space-y-2 text-sm">
        {location && <Row label="Location" value={location.name} />}
        <Row label="Service" value={service.name} />
        {provider && <Row label="Provider" value={provider} />}
        {date && <Row label="Date" value={new Date(date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} />}
        {time && <Row label="Time" value={time} />}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
          <span className="font-['Outfit'] font-semibold text-slate-900">Total</span>
          <span className="font-['Outfit'] font-semibold text-slate-900 text-lg">${service.price}</span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-400 font-['Manrope']">{label}</span>
      <span className={`text-slate-900 font-['Manrope'] text-right ${mono ? "font-mono text-xs" : "font-medium"}`}>{value}</span>
    </div>
  );
}
